import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, PowerSettingsNew, PowerOff } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListPageHeader } from '@/components/ListPageHeader';
import { TenantSelector, DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';
import { EntitlementStatusBadge } from '@/components/domain-engine/EntitlementStatusBadge';
import { GrantEntitlementWizard } from '@/components/domain-engine/GrantEntitlementWizard';
import { RevokeEntitlementDialog } from '@/components/domain-engine/RevokeEntitlementDialog';
import { EditEntitlementDialog } from '@/components/domain-engine/EditEntitlementDialog';
import { getDomainIcon } from '@/components/domain-engine/domain-icons';
import { fetchApi } from '@/api/fetchApi';

interface EntitlementRow {
  id: string;
  domainId: string;
  tenantId: string;
  name: string;
  billingTier: string;
  description: string;
  level: string;
  status: string;
  grantedAt?: string;
  expiresAt?: string | null;
  trialDays?: number;
  paid?: boolean;
}

export function EntitlementListPage() {
  const [tenantId, setTenantId] = useState(DEFAULT_TENANT_ID);
  const [statusFilter, setStatusFilter] = useState('');
  const [grantOpen, setGrantOpen] = useState(false);
  const [editRow, setEditRow] = useState<EntitlementRow | null>(null);
  const [revokeRow, setRevokeRow] = useState<EntitlementRow | null>(null);
  const queryClient = useQueryClient();

  const { data: entitlements, isLoading: loadingEnt, error: errorEnt } = useQuery({
    queryKey: ['entitlements', tenantId],
    queryFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements`),
    staleTime: 5 * 60 * 1000,
    enabled: !!tenantId,
  });

  const { data: domains, isLoading: loadingDomains, error: errorDomains } = useQuery({
    queryKey: ['domains'],
    queryFn: () => fetchApi('/domains'),
    staleTime: 5 * 60 * 1000,
  });

  const enrichedEntitlements = useMemo(() => {
    const entList = (Array.isArray(entitlements) ? entitlements : []) as Array<{
      domainId: string; status: string; grantedAt?: string;
      expiresAt?: string | null; trialDays?: number; paid?: boolean;
    }>;
    const domainList = (Array.isArray(domains) ? domains : []) as Array<{
      domainId: string; name: string; billingTier: string;
      description: string; level: string; status: string;
    }>;

    const allDomains = domainList.filter((d) => d.level !== 'feature');
    const entMap = new Map(entList.map((e) => [e.domainId, e]));

    return allDomains.map((d) => {
      const ent = entMap.get(d.domainId);
      return {
        id: ent ? `${tenantId}:${d.domainId}` : d.domainId,
        domainId: d.domainId,
        tenantId,
        name: d.name,
        billingTier: d.billingTier,
        description: d.description,
        level: d.level,
        status: ent?.status || '—',
        grantedAt: ent?.grantedAt,
        expiresAt: ent?.expiresAt,
        trialDays: ent?.trialDays,
        paid: ent?.paid,
      } as EntitlementRow;
    });
  }, [entitlements, domains, tenantId]);

  const filteredEntitlements = useMemo(() => {
    if (!statusFilter) return enrichedEntitlements;
    return enrichedEntitlements.filter((e) => e.status === statusFilter);
  }, [enrichedEntitlements, statusFilter]);

  const metrics = useMemo(() => {
    const counts = { Active: 0, Trial: 0, Suspended: 0, Expired: 0 };
    enrichedEntitlements.forEach((e) => {
      if (counts[e.status as keyof typeof counts] !== undefined) {
        counts[e.status as keyof typeof counts]++;
      }
    });
    return [
      { label: 'Active', value: counts.Active, color: '#2e7d32' },
      { label: 'Trial', value: counts.Trial, color: '#1565c0' },
      { label: 'Suspended', value: counts.Suspended, color: '#ed6c02' },
      { label: 'Expired', value: counts.Expired, color: '#757575' },
    ];
  }, [enrichedEntitlements]);

  const handleQuickToggle = async (row: EntitlementRow) => {
    if (row.status === 'Active' || row.status === 'Trial') {
      // Deactivate via PATCH
      await fetchApi(`/domains/tenants/${tenantId}/entitlements/${row.domainId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'Expired' }),
      });
    } else {
      // Reactivate — use grant which re-activates expired
      await fetchApi(`/domains/tenants/${tenantId}/entitlements/grant`, {
        method: 'POST',
        body: JSON.stringify({ domainId: row.domainId, paid: row.paid ?? false }),
      });
    }
    queryClient.invalidateQueries({ queryKey: ['entitlements', tenantId] });
  };

  if (loadingEnt || loadingDomains) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorEnt || errorDomains) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load entitlement data. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <ListPageHeader
        title="Entitlement Management"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TenantSelector value={tenantId} onChange={setTenantId} />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setGrantOpen(true)}
            >
              Grant Entitlement
            </Button>
          </Box>
        }
        metrics={metrics}
      />

      <Box sx={{ px: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {['', 'Active', 'Trial', 'Suspended', 'Expired'].map((s) => (
            <Button
              key={s || 'all'}
              size="small"
              variant={statusFilter === s ? 'contained' : 'outlined'}
              onClick={() => setStatusFilter(s)}
            >
              {s || 'All'}
            </Button>
          ))}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mx: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell>Granted</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEntitlements.map((ent) => {
              const isActive = ent.status === 'Active' || ent.status === 'Trial';
              return (
                <TableRow key={ent.domainId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getDomainIcon(ent.domainId)}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{ent.name || ent.domainId}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{ent.domainId}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><EntitlementStatusBadge status={ent.status} /></TableCell>
                  <TableCell>{ent.billingTier}</TableCell>
                  <TableCell>{ent.grantedAt ? new Date(ent.grantedAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{ent.expiresAt ? new Date(ent.expiresAt).toLocaleDateString() : 'Perpetual'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={isActive ? 'Quick deactivate' : 'Quick activate'}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuickToggle(ent)}
                        color={isActive ? 'warning' : 'success'}
                      >
                        {isActive ? <PowerOff fontSize="small" /> : <PowerSettingsNew fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit details">
                      <IconButton size="small" onClick={() => setEditRow(ent)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isActive && (
                      <Tooltip title="Revoke">
                        <IconButton size="small" color="error" onClick={() => setRevokeRow(ent)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredEntitlements.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No entitlements found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Grant Wizard — rendered inline when open */}
      {grantOpen && (
        <GrantEntitlementWizard
          tenantId={tenantId}
          onCancel={() => setGrantOpen(false)}
          onComplete={() => setGrantOpen(false)}
        />
      )}

      {/* Edit Dialog */}
      {editRow && (
        <EditEntitlementDialog
          tenantId={tenantId}
          domainId={editRow.domainId}
          domainName={editRow.name}
          currentStatus={editRow.status}
          currentExpiresAt={editRow.expiresAt}
          currentPaid={editRow.paid}
          open={!!editRow}
          onClose={() => setEditRow(null)}
        />
      )}

      {/* Revoke Dialog */}
      {revokeRow && (
        <RevokeEntitlementDialog
          tenantId={tenantId}
          domainId={revokeRow.domainId}
          domainName={revokeRow.name}
          open={!!revokeRow}
          onClose={() => setRevokeRow(null)}
        />
      )}
    </Box>
  );
}
